import React, { useCallback, useMemo } from "react";
import { ColorValue, StyleSheet, View, ViewStyle } from "react-native";
import { useTheme } from "../hooks/use-theme";
import MarkdownView from "../components/MarkdownView";
import { useNavigation } from "@react-navigation/native";
import { StackScreenProps } from "@react-navigation/stack";
import { RootStackParamList } from "../navigations/RootNavigation";
import IconButton from "../components/IconButton";
import Icon, { IconName } from "../components/Icon";

export type CalloutMode = "info" | "warning" | "error" | "success";

export type CalloutProps = {
    markdown: string;
    mode?: CalloutMode;
    style?: ViewStyle;
    onLinkPress?: (link: string) => boolean;
    onClose?: () => void;
    testID?: string;
    icon?: IconName;
};

type ScreenParams = StackScreenProps<RootStackParamList>;

const Callout: React.FC<CalloutProps> = ({
    markdown,
    mode,
    style,
    onLinkPress: propsOnLinkPress,
    testID,
    onClose,
    icon,
}) => {
    const navigation = useNavigation<ScreenParams["navigation"]>();

    const { colors } = useTheme();
    const styles = useStylesheet();
    const background = useMemo(() => {
        switch (mode) {
            case "warning":
                return colors.warningBackground;
            case "error":
                return colors.errorBackground;
            case "info":
                return colors.infoBackground;
            case "success":
                return colors.successBackground;
            default:
                return colors.infoBackground;
        }
    }, [colors, mode]);
    const onBackground = useMemo(() => {
        switch (mode) {
            case "warning":
                return colors.onWarningBackground;
            case "error":
                return colors.onErrorBackground;
            case "info":
                return colors.onInfoBackground;
            case "success":
                return colors.onSuccessBackground;
            default:
                return colors.onInfoBackground;
        }
    }, [colors, mode]);

    const onLinkPress = useCallback(
        (url: string) => {
            if (url.startsWith("http")) {
                return true;
            }
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            navigation.navigate<any>(url);
            return false;
        },
        [navigation],
    );
    const markdownStyle = useCalloutMarkdownStyle({ fontColor: onBackground });

    return (
        <View
            testID={testID}
            style={[
                styles.callout,
                {
                    backgroundColor: background,
                },
                style,
            ]}
        >
            {icon && <Icon name={icon} style={styles.leftIcon} size={20} color={onBackground} />}
            <MarkdownView
                style={styles.markdown}
                markdown={markdown}
                markdownStyle={markdownStyle}
                onLinkPress={propsOnLinkPress || onLinkPress}
                fontColor={onBackground}
            />
            {onClose && (
                <IconButton
                    icon="close"
                    onPress={onClose}
                    style={styles.closeButton}
                    size={16}
                    iconColor={onBackground}
                />
            )}
        </View>
    );
};

export const useCalloutMarkdownStyle = ({ fontColor }: { fontColor: ColorValue }) => {
    const { fonts, gridSize, colors } = useTheme();
    return useMemo(() => {
        return StyleSheet.create({
            // The main container

            // @pseudo class, does not have a unique render rule
            heading1: {
                fontWeight: "bold",
                marginBottom: gridSize / 4,
                color: fontColor,
                ...fonts.titleLarge,
                fontSize: 30,
            },
            heading2: {
                fontWeight: "bold",
                marginBottom: gridSize / 4,
                color: fontColor,
                ...fonts.titleMedium,
                fontSize: 18,
            },
            heading3: {
                fontWeight: "bold",
                marginBottom: gridSize / 4,
                color: fontColor,
                ...fonts.titleSmall,
                fontSize: 15,
            },
            heading4: {
                color: fontColor,
                marginBottom: gridSize / 4,
                ...fonts.bodyLarge,
                fontSize: 14,
            },
            heading5: {
                color: fontColor,
                marginBottom: gridSize / 4,
                ...fonts.bodyMedium,
                fontSize: 14,
            },
            heading6: {
                color: fontColor,
                marginBottom: gridSize / 4,
                ...fonts.bodySmall,
                fontSize: 11,
            },
            bullet_list: {
                color: fontColor,
                ...fonts.bodyMedium,
                lineHeight: 20, // same as normal small line height
                fontSize: 14,
            },
            ordered_list: {
                color: fontColor,
                ...fonts.bodyMedium,
                lineHeight: 20, // same as normal small line height
                fontSize: 14,
            },
            paragraph: {
                marginTop: 0,
                marginBottom: 0,
                ...fonts.bodyMedium,
                color: fontColor,
                fontSize: 14,
                lineHeight: 20,
            },
            bold: {
                ...fonts.bodyLarge,
                fontSize: 14,
                lineHeight: 20,
            },
            strong: {
                ...fonts.bodyLarge,
                fontSize: 14,
                lineHeight: 20,
            },
            bullet_list_icon: {
                marginLeft: 0,
                marginRight: 10,
            },
            hardbreak: {
                width: "100%",
                height: 1,
            },
            link: {
                color: colors.secondary,
            },
            blocklink: {
                color: colors.secondary,
            },
            helpButton: {
                margin: 0,
                marginHorizontal: 4,
                borderColor: colors.warningBorder,
                borderWidth: 1,
                backgroundColor: colors.warningBackground,
            },
        });
    }, [fontColor, fonts, colors, gridSize]);
};

const useStylesheet = () => {
    const theme = useTheme();
    return React.useMemo(() => {
        return StyleSheet.create({
            callout: {
                alignSelf: "stretch",
                paddingTop: 4,
                paddingBottom: 4,
                paddingHorizontal: theme.gridSize / 2,
                borderRadius: theme.roundness,
                flexDirection: "row",
            },
            markdown: {
                flex: 1,
                flexGrow: 1,
            },
            closeButton: {
                flex: 0,
                flexGrow: 1,
                flexShrink: 0,
                margin: 0,
                marginRight: -10,
                marginTop: -3,
                padding: 0,
            },
            leftIcon: {
                flex: 0,
                flexShrink: 0,
                margin: 0,
                marginLeft: -10,
                marginRight: 0,
                marginTop: -3,
                padding: 0,
            },
        });
    }, [theme]);
};

export default Callout;
